import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Repeat, MapPin, Plus, X, AlertTriangle, Check, Truck, Package, Loader2, MessageCircle, List as ListIcon, Map as MapIcon } from 'lucide-react';
import axios from 'axios';
import MarketplaceMap from './MarketplaceMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_STYLE = {
  offer: 'bg-[#6B9F4A]/15 text-[#1A4331]',
  wanted: 'bg-[#C9A85B]/20 text-[#7A5E1F]',
};

export default function Marketplace({ user, focusListingId }) {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [listings, setListings] = useState([]);
  const [scope, setScope] = useState('region');
  const [type, setType] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [view, setView] = useState('list');
  const [focused, setFocused] = useState(null);
  const [highlightId, setHighlightId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/marketplace/meta`, { withCredentials: true })
      .then((r) => setMeta(r.data))
      .catch(() => setMeta(null));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { scope };
      if (type) params.type = type;
      if (category) params.category = category;
      const r = await axios.get(`${API}/marketplace/listings`, { params, withCredentials: true });
      setListings(r.data.listings || []);
    } catch { setListings([]); }
    finally { setLoading(false); }
  }, [scope, type, category]);

  useEffect(() => { load(); }, [load]);

  // Deep-link from the home Swap & Sell carousel — surface that listing at the top, highlighted.
  useEffect(() => {
    if (!focusListingId) { setFocused(null); setHighlightId(null); return; }
    setView('list'); setScope('all'); setHighlightId(focusListingId);
    axios.get(`${API}/marketplace/listings/${focusListingId}`, { withCredentials: true })
      .then((r) => { if (r.data?.id) setFocused(r.data); }).catch(() => {});
    const h = setTimeout(() => setHighlightId(null), 2600);
    return () => clearTimeout(h);
  }, [focusListingId]);

  const requestSwap = async (l) => {
    try {
      const r = await axios.post(`${API}/marketplace/listings/${l.id}/swap-request`, {}, { withCredentials: true });
      setListings((prev) => prev.map((x) => x.id === l.id ? { ...x, swap_requested: true } : x));
      setToast({ kind: 'success', text: `Swap requested with ${l.display_name}.`, ownerId: r.data?.owner_id });
    } catch (e) {
      setToast({ kind: 'error', text: e.response?.data?.detail || 'Could not request swap.' });
    }
  };

  if (!meta) return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#8EAA97]" /></div>;

  const display = focused ? [focused, ...listings.filter((x) => x.id !== focused.id)] : listings;

  return (
    <div className="space-y-4" data-testid="marketplace">
      {/* Warning banner */}
      <div className="bg-[#C66B4A]/10 border border-[#C66B4A]/25 rounded-2xl p-4 flex items-start gap-3" data-testid="market-warning">
        <AlertTriangle className="w-5 h-5 text-[#C66B4A] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-[#2A3528]">{meta.warning_banner}</p>
      </div>

      {toast && (
        <div className={`rounded-2xl px-4 py-3 text-sm font-semibold flex items-center gap-2 ${toast.kind === 'success' ? 'bg-[#6B9F4A]/15 text-[#1A4331]' : 'bg-[#C66B4A]/12 text-[#C66B4A]'}`} data-testid="market-toast">
          {toast.kind === 'success' && <Check className="w-4 h-4" />} {toast.text}
          {toast.ownerId && (
            <button onClick={() => navigate(`/messages/${toast.ownerId}`)} data-testid="market-message-grower"
              className="hg-press inline-flex items-center gap-1 bg-[#1A4331] text-[#FAF6EC] rounded-full px-3 py-1 text-xs">
              <MessageCircle className="w-3.5 h-3.5" /> Message grower
            </button>
          )}
          <button onClick={() => setToast(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* View toggle */}
      <div className="flex items-center gap-2">
        <div className="flex bg-[#FAF6EC] rounded-full p-1 border border-[#1A4331]/10" data-testid="market-view-toggle">
          <button onClick={() => setView('list')} data-testid="market-view-list" className={`flex items-center gap-1 text-xs font-semibold px-3.5 py-1.5 rounded-full transition-colors ${view === 'list' ? 'bg-[#1A4331] text-[#FAF6EC]' : 'text-[#7A6B57]'}`}><ListIcon className="w-3.5 h-3.5" /> List</button>
          <button onClick={() => setView('map')} data-testid="market-view-map" className={`flex items-center gap-1 text-xs font-semibold px-3.5 py-1.5 rounded-full transition-colors ${view === 'map' ? 'bg-[#1A4331] text-[#FAF6EC]' : 'text-[#7A6B57]'}`}><MapIcon className="w-3.5 h-3.5" /> Map</button>
        </div>
      </div>

      {view === 'map' && <MarketplaceMap user={user} onSwapToast={setToast} />}

      {view === 'list' && (
      <>
      {/* Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex bg-[#FAF6EC] rounded-full p-1 border border-[#1A4331]/10">
          <button onClick={() => setScope('region')} data-testid="market-scope-region" className={`flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${scope === 'region' ? 'bg-[#1A4331] text-[#FAF6EC]' : 'text-[#7A6B57]'}`}>
            <MapPin className="w-3.5 h-3.5" /> {meta.region?.label || 'My region'}
          </button>
          <button onClick={() => setScope('all')} data-testid="market-scope-all" className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${scope === 'all' ? 'bg-[#1A4331] text-[#FAF6EC]' : 'text-[#7A6B57]'}`}>All regions</button>
        </div>
        <button onClick={() => setSheetOpen(true)} data-testid="market-new-listing" className="hg-press ml-auto inline-flex items-center gap-1.5 bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] text-sm font-semibold px-4 py-2 rounded-full">
          <Plus className="w-4 h-4" /> List a swap or request
        </button>
      </div>

      {/* Type + category chips */}
      <div className="flex gap-2 overflow-x-auto hg-scroll pb-1">
        {[{ id: '', label: 'All' }, ...meta.types].map((t) => (
          <button key={t.id || 'all'} onClick={() => setType(t.id)} data-testid={`market-type-${t.id || 'all'}`} className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${type === t.id ? 'bg-[#6B9F4A] text-white border-[#6B9F4A]' : 'bg-white text-[#1A4331] border-[#1A4331]/15'}`}>{t.label}</button>
        ))}
        <span className="w-px bg-[#1A4331]/10 mx-1" />
        {[{ id: '', label: 'All types' }, ...meta.categories].map((c) => (
          <button key={c.id || 'allcat'} onClick={() => setCategory(c.id)} data-testid={`market-cat-${c.id || 'all'}`} className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${category === c.id ? 'bg-[#1A4331] text-[#FAF6EC] border-[#1A4331]' : 'bg-white text-[#1A4331] border-[#1A4331]/15'}`}>{c.label}</button>
        ))}
      </div>

      {/* Listings */}
      {loading ? (
        <div className="grid md:grid-cols-2 gap-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-40 rounded-3xl bg-white/60 animate-pulse" />)}</div>
      ) : display.length === 0 ? (
        <div className="bg-[#FAF6EC] rounded-3xl p-10 text-center border border-[#1A4331]/8" data-testid="market-empty">
          <Repeat className="w-10 h-10 text-[#8EAA97] mx-auto mb-3" />
          <p className="text-[#7A6B57]">No listings here yet — be the first to offer a swap, or try All regions.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="market-listings">
          {display.map((l) => (
            <div key={l.id} data-testid={`market-listing-${l.id}`} className={`bg-[#FAF6EC] rounded-3xl border overflow-hidden transition-shadow ${highlightId === l.id ? 'ring-2 ring-[#6B9F4A] border-transparent' : l.status === 'swapped' ? 'border-[#6B9F4A]/40 opacity-80' : 'border-[#1A4331]/10'}`}>
              {l.photo_urls?.[0] && <img src={l.photo_urls[0]} alt={l.title} loading="lazy" className="w-full h-36 object-cover" />}
              <div className="p-5">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${TYPE_STYLE[l.type] || 'bg-[#E5EBDD] text-[#7A6B57]'}`}>{l.type === 'offer' ? 'Offering' : 'Wanted'}</span>
                  <span className="text-[11px] text-[#7A6B57] capitalize">{l.category}</span>
                  <span className="inline-flex items-center gap-1 text-[11px] text-[#7A6B57]">{l.delivery === 'postage' ? <Truck className="w-3 h-3" /> : <Package className="w-3 h-3" />} {l.delivery}</span>
                </div>
                <h4 className="text-lg font-bold text-[#1A4331] leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{l.title}</h4>
                {(l.variety || l.quantity) && <p className="text-sm text-[#5C665C] mt-0.5">{[l.variety, l.quantity].filter(Boolean).join(' · ')}</p>}
                {l.description && <p className="text-sm text-[#2A3528] mt-1 line-clamp-2 leading-relaxed">{l.description}</p>}
                <div className="flex items-center gap-2 mt-3 text-xs text-[#7A6B57]">
                  <span className="font-semibold text-[#1A4331]">{l.display_name}</span>
                  {l.region_label && <><span>·</span><span>{l.region_label}</span></>}
                </div>
                <div className="mt-4">
                  {l.status === 'swapped' ? (
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#6B9F4A]"><Check className="w-4 h-4" /> Swapped</span>
                  ) : l.is_mine ? (
                    <span className="text-xs font-semibold text-[#7A6B57]">Your listing{l.request_count ? ` · ${l.request_count} request${l.request_count > 1 ? 's' : ''}` : ''}</span>
                  ) : l.swap_requested ? (
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#6B9F4A]" data-testid={`market-requested-${l.id}`}><Check className="w-4 h-4" /> Swap requested</span>
                  ) : (
                    <button onClick={() => requestSwap(l)} data-testid={`market-request-${l.id}`} className="hg-press bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5">
                      <Repeat className="w-4 h-4" /> Request swap
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {sheetOpen && (
        <NewListingSheet meta={meta} onClose={() => setSheetOpen(false)} onCreated={() => { setSheetOpen(false); load(); setToast({ kind: 'success', text: 'Listing posted!' }); }} setMeta={setMeta} />
      )}
    </div>
  );
}

function NewListingSheet({ meta, onClose, onCreated, setMeta }) {
  const [accepted, setAccepted] = useState(meta.agreement_accepted);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ type: 'offer', category: meta.categories?.[0]?.id || '', title: '', variety: '', quantity: '', delivery: meta.delivery?.[0]?.id || 'pickup', description: '' });

  const acceptAgreement = async () => {
    setChecking(true);
    try {
      await axios.post(`${API}/marketplace/agreement/accept`, {}, { withCredentials: true });
      setAccepted(true);
      setMeta((m) => ({ ...m, agreement_accepted: true }));
    } catch { setErr('Could not accept the agreement.'); }
    finally { setChecking(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      await axios.post(`${API}/marketplace/listings`, form, { withCredentials: true });
      onCreated();
    } catch (e2) { setErr(e2.response?.data?.detail || 'Could not post listing.'); setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-[#0F2D1F]/45 backdrop-blur-sm flex items-end md:items-center justify-center p-4" onClick={onClose} data-testid="market-sheet">
      <div className="bg-[#FAF6EC] rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-2xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>List a swap or request</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white flex items-center justify-center"><X className="w-5 h-5 text-[#5C665C]" /></button>
        </div>

        <div className="bg-[#C66B4A]/10 border border-[#C66B4A]/25 rounded-2xl p-3 flex items-start gap-2 mb-4">
          <AlertTriangle className="w-4 h-4 text-[#C66B4A] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[#2A3528]">{meta.warning_banner}</p>
        </div>

        {err && <p className="text-sm text-[#C66B4A] font-semibold mb-3">{err}</p>}

        {!accepted ? (
          <div data-testid="market-agreement">
            <p className="text-sm text-[#2A3528] leading-relaxed whitespace-pre-line mb-4">{meta.agreement_text}</p>
            <button onClick={acceptAgreement} disabled={checking} data-testid="market-accept-agreement" className="w-full bg-[#1A4331] text-[#FAF6EC] font-semibold py-3 rounded-full flex items-center justify-center gap-2">
              {checking ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />} I agree — continue
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="flex gap-2">
              {meta.types.map((t) => (
                <button key={t.id} type="button" onClick={() => setForm((f) => ({ ...f, type: t.id }))} data-testid={`new-type-${t.id}`} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border ${form.type === t.id ? 'bg-[#1A4331] text-[#FAF6EC] border-[#1A4331]' : 'bg-white text-[#1A4331] border-[#1A4331]/15'}`}>{t.label}</button>
              ))}
            </div>
            <input required placeholder="Title (e.g. Spare tomato seedlings)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="new-title" className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]" />
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="new-category" className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]">
              {meta.categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Variety (optional)" value={form.variety} onChange={(e) => setForm({ ...form, variety: e.target.value })} className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]" />
              <input placeholder="Quantity (optional)" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]" />
            </div>
            <select value={form.delivery} onChange={(e) => setForm({ ...form, delivery: e.target.value })} data-testid="new-delivery" className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]">
              {meta.delivery.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
            <textarea placeholder="Description (optional)" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]" />
            <button type="submit" disabled={submitting} data-testid="new-submit" className="w-full bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] font-semibold py-3 rounded-full disabled:opacity-60 flex items-center justify-center gap-2">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />} Post listing
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
